import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";

import StorefrontIcon from "@mui/icons-material/Storefront";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CalculateIcon from "@mui/icons-material/Calculate";
import PaymentsIcon from "@mui/icons-material/Payments";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { API } from "../../../utils/Api";
import axios from "axios";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const monthInputToMMYYYY = (yyyyMm) => {
  if (!yyyyMm || typeof yyyyMm !== "string" || !yyyyMm.includes("-")) return "";
  const [yyyy, mm] = yyyyMm.split("-");
  if (!yyyy || !mm) return "";
  return `${mm}-${yyyy}`;
};

const mmYYYYToMonthInput = (mmYYYY) => {
  if (!mmYYYY || typeof mmYYYY !== "string" || !mmYYYY.includes("-")) return "";
  const [mm, yyyy] = mmYYYY.split("-");
  if (!yyyy || !mm) return "";
  return `${yyyy}-${mm}`;
};

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const FoodDeliveryAppPOS = () => {
  const accessToken = localStorage.getItem("authToken");
  const { access, email } = useSelector((state) => state.login);

  const [deliveryAccess, setDeliveryAccess] = useState([]);

  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [salesList, setSalesList] = useState([]);

  const [filterMonth, setFilterMonth] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  });

  const [formMonth, setFormMonth] = useState(filterMonth);

  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [formData, setFormData] = useState({
    outletId: "",
    app: "ZOMATO",
    saleFromDate: "",
    saleToDate: "",
    monthYear: monthInputToMMYYYY(filterMonth),
    totalSaleValue: "",
    deliveredCount: "",
    cancelledCount: "",
    totalOrders: "",
    deductions: "",
    totalPayouts: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [salesId, setSalesId] = useState("");
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const apps = ["ZOMATO", "SWIGGY"];

  useEffect(() => {
    if (Array.isArray(access)) {
      const filteredAccess = access.filter((perm) =>
        perm?.startsWith("FoodAppDelivery:")
      );
      setDeliveryAccess(filteredAccess);
    }
  }, [access]);

  const canAdd = deliveryAccess.includes("FoodDeliveryApp:add") || email === "contact@xpansecafe.com";
  const canUpdate = deliveryAccess.includes("FoodDeliveryApp:update") || email === "contact@xpansecafe.com";
  const canDelete = deliveryAccess.includes("FoodDeliveryApp:delete") || email === "contact@xpansecafe.com";

  const handleError = (error) => {
    let errorMessage = "Something went wrong. Please try again.";
    if (error?.response) {
      if (error.response?.status === 406) {
        errorMessage =
          error?.response?.data?.error?.message || error?.response?.data?.error;
      } else if (error.response?.status === 412) {
        errorMessage = error?.response?.data?.data?.message;
      } else if (error.response?.status === 500) {
        errorMessage = error?.response?.data?.error;
      }
    }
    toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "light" });
  };

  const getFoodDeliveryAppSales = async (page = currentPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const monthYear = monthInputToMMYYYY(filterMonth);

      const params = { page, limit, monthYear };

      const response = await axios.get(`${API.BASE_URL}food-delivery-apps-sales/list`, {
        headers,
        params,
      });

      if (response?.data?.data?.length > 0) {
        setSalesList(response.data.data);

        if (response?.data?.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotalRecords(response.data.pagination.totalRecords);
          setCurrentPage(response.data.pagination.page);
        } else {
          setTotalPages(Math.ceil(response.data.data.length / limit));
          setTotalRecords(response.data.data.length);
        }
      } else {
        setSalesList([]);
        setTotalPages(1);
        setTotalRecords(0);
        setCurrentPage(1);
        toast.info("No records found.", { position: "top-right", autoClose: 3000, theme: "light" });
      }
    } catch (error) {
      console.error(error);
      handleError(error);
      setTotalPages(1);
      setTotalRecords(0);
      setCurrentPage(1);
      setSalesList([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormMonth(filterMonth);
    setFormData({
      outletId: "",
      app: "ZOMATO",
      saleFromDate: "",
      saleToDate: "",
      monthYear: monthInputToMMYYYY(filterMonth),
      totalSaleValue: "",
      deliveredCount: "",
      cancelledCount: "",
      totalOrders: "",
      deductions: "",
      totalPayouts: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormMonthChange = (valueYYYYMM) => {
    setFormMonth(valueYYYYMM);
    setFormData((prev) => ({ ...prev, monthYear: monthInputToMMYYYY(valueYYYYMM) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const payload = {
        ...formData,
        outletId: formData.outletId,
        totalSaleValue: num(formData.totalSaleValue),
        deliveredCount: parseInt(formData.deliveredCount || 0, 10),
        cancelledCount: parseInt(formData.cancelledCount || 0, 10),
        totalOrders: parseInt(formData.totalOrders || 0, 10),
        deductions: num(formData.deductions),
        totalPayouts: num(formData.totalPayouts),
        monthYear: formData.monthYear,
      };

      let response;
      if (editingRecord) {
        response = await axios.put(
          `food-delivery-apps-sales/`,
          { ...payload, _id: editingRecord._id },
          { headers }
        );
      } else {
        response = await axios.post(`food-delivery-apps-sales/`, payload, { headers });
      }

      if (response?.data?.status === true) {
        toast.success(editingRecord ? "Record updated successfully" : "Record created successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
        setShowModal(false);
        setEditingRecord(null);
        resetForm();
        getFoodDeliveryAppSales(currentPage, itemsPerPage);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);

    const recordMonthInput = mmYYYYToMonthInput(record.monthYear); // "YYYY-MM"
    setFormMonth(recordMonthInput || filterMonth);

    setFormData({
      outletId: record.outletId?._id || record.outletId,
      app: record.app,
      saleFromDate: record.saleFromDate || "",
      saleToDate: record.saleToDate || "",
      monthYear: record.monthYear,
      totalSaleValue: record.totalSaleValue,
      deliveredCount: record.deliveredCount,
      cancelledCount: record.cancelledCount,
      totalOrders: record.totalOrders,
      deductions: record.deductions,
      totalPayouts: record.totalPayouts,
    });

    setShowModal(true);
  };

  const handleOpenDeleteModal = (id) => {
    setSalesId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    setDeleteBtnLoading(true);
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const response = await axios.delete(`food-delivery-apps-sales`, {
        headers,
        data: { id: salesId },
      });

      if (response?.data?.status === true) {
        toast.success("Record deleted successfully", { position: "top-right", autoClose: 3000, theme: "light" });
        setShowDeleteConfirm(false);
        getFoodDeliveryAppSales(currentPage, itemsPerPage);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      setDeleteBtnLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      getFoodDeliveryAppSales(pageNumber, itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    getFoodDeliveryAppSales(1, limit);
  };

  useEffect(() => {
    getFoodDeliveryAppSales(1, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth]);

  const stats = useMemo(() => {
    const totalSales = salesList.reduce((sum, item) => sum + num(item.totalSaleValue), 0);
    const totalOrders = salesList.reduce((sum, item) => sum + parseInt(item.totalOrders || 0, 10), 0);
    const totalPayouts = salesList.reduce((sum, item) => sum + num(item.totalPayouts), 0);
    const totalDeductions = salesList.reduce((sum, item) => sum + num(item.deductions), 0);
    const deliveredCount = salesList.reduce((sum, item) => sum + parseInt(item.deliveredCount || 0, 10), 0);
    const cancelledCount = salesList.reduce((sum, item) => sum + parseInt(item.cancelledCount || 0, 10), 0);
    const successRate = totalOrders > 0 ? ((deliveredCount / totalOrders) * 100).toFixed(2) : "0.00";

    return {
      totalSales,
      totalOrders,
      totalPayouts,
      totalDeductions,
      deliveredCount,
      cancelledCount,
      successRate,
    };
  }, [salesList]);

  const appChipColor = (app) => {
    if (app === "ZOMATO") return "error";
    if (app === "SWIGGY") return "warning";
    return "default";
  };

  const PaginationBar = () => (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "stretch", md: "center" }}
      justifyContent="space-between"
      sx={{ mt: 2 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Rows:</Typography>
        <TextField
          select
          size="small"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
          sx={{ width: 120 }}
        >
          {[5, 10, 20, 50, 100].map((size) => (
            <MenuItem key={size} value={size}>
              {size} / page
            </MenuItem>
          ))}
        </TextField>

        <Typography sx={{ fontSize: 13, opacity: 0.7 }}>
          Showing {totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-
          {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <IconButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeftIcon />
        </IconButton>

        <TextField
          size="small"
          type="number"
          value={currentPage}
          inputProps={{ min: 1, max: totalPages }}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            if (v >= 1 && v <= totalPages) handlePageChange(v);
          }}
          sx={{ width: 90 }}
        />

        <Typography sx={{ fontSize: 13, opacity: 0.7 }}>
          / {totalPages}
        </Typography>

        <IconButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    </Stack>
  );

  return (
    <React.Fragment>
      <Box sx={{ p: 2, paddingTop: '120px' }}>
        {/* Header Row */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <StorefrontIcon />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Food Delivery App Sales
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 13, opacity: 0.7 }}>
              Manage your food delivery app sales and payouts
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Month Filter (native month input) */}
            <TextField
              size="small"
              label="Month"
              type="month"
              value={filterMonth}
              onChange={(e) => {
                setFilterMonth(e.target.value);
                setCurrentPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />

            {canAdd && (
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setEditingRecord(null);
                  resetForm();
                  setShowModal(true);
                }}
              >
                Add Record
              </Button>
            )}

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={() => getFoodDeliveryAppSales(currentPage, itemsPerPage)}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <CurrencyRupeeIcon sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Total Sales</Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  ₹{stats.totalSales.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <PaymentsIcon sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Total Payouts</Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  ₹{stats.totalPayouts.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <CheckCircleIcon sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Delivered</Typography>
                <Typography sx={{ fontWeight: 900 }}>{stats.deliveredCount}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <HighlightOffIcon sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Cancelled</Typography>
                <Typography sx={{ fontWeight: 900 }}>{stats.cancelledCount}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <ShoppingCartIcon sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>Total Orders</Typography>
                <Typography sx={{ fontWeight: 900 }}>{stats.totalOrders}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <CircularProgress />
                <Typography sx={{ mt: 1, opacity: 0.7 }}>Loading data...</Typography>
              </Box>
            ) : salesList.length === 0 ? (
              <Alert severity="info">
                No records found for the selected month.
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Outlet</TableCell>
                        <TableCell>App</TableCell>
                        <TableCell align="center">Month</TableCell>
                        <TableCell align="right">Sales Value</TableCell>
                        <TableCell align="center">Orders</TableCell>
                        <TableCell align="right">Deductions</TableCell>
                        <TableCell align="right">Payouts</TableCell>
                        {(canUpdate || canDelete) && (
                          <TableCell align="center">Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {salesList.map((record, index) => (
                        <TableRow key={record._id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 800 }}>
                              {record.outletId?.name}
                            </Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                              {record.outletId?.city}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={record.app}
                              color={appChipColor(record.app)}
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>
                          <TableCell align="center">{record.monthYear}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>
                            ₹{num(record.totalSaleValue).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={0.3} alignItems="center">
                              <Typography sx={{ fontSize: 12, color: "success.main" }}>
                                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {record.deliveredCount || 0}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: "error.main" }}>
                                <HighlightOffIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {record.cancelledCount || 0}
                              </Typography>
                              <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                                Total: {record.totalOrders}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ color: "error.main" }}>
                            ₹{num(record.deductions).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, color: "primary.main" }}>
                            ₹{num(record.totalPayouts).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>

                          {(canUpdate || canDelete) && (
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                {canUpdate && (
                                  <IconButton size="small" onClick={() => handleEdit(record)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {canDelete && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDeleteModal(record._id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && <PaginationBar />}
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRecord(null);
          resetForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {editingRecord ? "Edit Sales Record" : "Add New Sales Record"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Outlet</InputLabel>
                  <Select
                    name="outletId"
                    value={formData.outletId}
                    label="Outlet"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">Select Outlet</MenuItem>
                    {outlets.map((o) => (
                      <MenuItem key={o._id} value={o._id}>
                        {o.name} - {o.city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Delivery App</InputLabel>
                  <Select
                    name="app"
                    value={formData.app}
                    label="Delivery App"
                    onChange={handleInputChange}
                    required
                  >
                    {apps.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Month Year - native month input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Month Year"
                  type="month"
                  value={formMonth}
                  onChange={(e) => handleFormMonthChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Sales Value (₹)"
                  name="totalSaleValue"
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  value={formData.totalSaleValue}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Delivered Count"
                  name="deliveredCount"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.deliveredCount}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cancelled Count"
                  name="cancelledCount"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.cancelledCount}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Total Orders"
                  name="totalOrders"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData.totalOrders}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Deductions (₹)"
                  name="deductions"
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  value={formData.deductions}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Payouts (₹)"
                  name="totalPayouts"
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  value={formData.totalPayouts}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              {/* Sale From/To Date - native date input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sale From Date"
                  name="saleFromDate"
                  type="date"
                  value={formData.saleFromDate || ""}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sale To Date"
                  name="saleToDate"
                  type="date"
                  value={formData.saleToDate || ""}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setShowModal(false);
                setEditingRecord(null);
                resetForm();
              }}
            >
              Cancel
            </Button>

            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Saving..." : editingRecord ? "Update Record" : "Create Record"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900, color: "error.main" }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            Are you sure you want to delete? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowDeleteConfirm(false)} disabled={deleteBtnLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteBtnLoading}>
            {deleteBtnLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default FoodDeliveryAppPOS;
